import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Horizontally scrollable row of cards (user-controlled; never auto-advances).
 * - Pointer layouts get previous/next buttons that page by the visible width and
 *   disable at each edge; they are hidden when everything fits and on coarse
 *   (touch) pointers, where swipe with scroll-snap and a peeking next card apply.
 * - Left/Right move focus between cards inside the row and keep the focused card
 *   in view. At the first/last card the key is not consumed, so navigation can
 *   leave the row. Focus from D-pad/gamepad/Tab scrolls the card into view.
 * - `resetKey` identifies the row's source (catalogue + profile scope). A new key
 *   returns the row to its start; the same key keeps the user's position.
 * Cards are the rail's direct children and keep their own actions.
 */
export function MediaRail({ label, resetKey, children, className = '', itemSelector = ':scope > *' }: {
  label: string; resetKey: string; children: ReactNode; className?: string; itemSelector?: string;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: true, end: true });
  const measure = useCallback(() => {
    const el = track.current; if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const start = el.scrollLeft <= 2, end = el.scrollLeft >= max - 2;
    setEdges(old => (old.start === start && old.end === end ? old : { start, end }));
    // An arrow that reaches its edge becomes disabled; if it held focus (some
    // browsers focus on click), hand focus to the edge card instead of the page.
    const active = document.activeElement as HTMLElement | null, rail = el.parentElement;
    if (active && rail?.contains(active) && active.classList.contains('rail-arrow') && ((start && active.classList.contains('rail-prev')) || (end && active.classList.contains('rail-next')))) {
      const list = [...el.querySelectorAll<HTMLElement>(itemSelector)], edge = start ? list[0] : list.at(-1);
      (edge?.matches('button,a[href],[tabindex]') ? edge : edge?.querySelector<HTMLElement>('button,a[href],[tabindex]'))?.focus({ preventScroll: true });
    }
  }, [itemSelector]);
  useLayoutEffect(() => { if (track.current) track.current.scrollLeft = 0; measure(); }, [resetKey, measure]);
  useEffect(() => {
    const el = track.current; if (!el) return;
    const observer = new ResizeObserver(measure); observer.observe(el);
    const mutations = new MutationObserver(measure); mutations.observe(el, { childList: true });
    el.addEventListener('scroll', measure, { passive: true });
    return () => { observer.disconnect(); mutations.disconnect(); el.removeEventListener('scroll', measure); };
  }, [measure]);
  const smooth = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth') as ScrollBehavior;
  const page = (direction: -1 | 1) => {
    const el = track.current; if (!el) return;
    el.scrollBy({ left: direction * Math.max(el.clientWidth * 0.85, 1), behavior: smooth() });
  };
  const items = () => [...(track.current?.querySelectorAll<HTMLElement>(itemSelector) || [])];
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if ((e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || e.nativeEvent.isComposing) return;
    const target = e.target as HTMLElement;
    if (target.closest('input,select,textarea,[contenteditable]')) return;
    const list = items(), index = list.findIndex(item => item.contains(target));
    if (index < 0) return;
    const next = list[index + (e.key === 'ArrowRight' ? 1 : -1)];
    if (!next) return; // edge: leave the key for surrounding navigation
    const focusable = next.matches('button,a[href],[tabindex]') ? next : next.querySelector<HTMLElement>('button,a[href],[tabindex]');
    if (!focusable) return;
    e.preventDefault();
    focusable.focus({ preventScroll: true });
    focusable.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: smooth() });
  };
  const overflow = !(edges.start && edges.end);
  return <div className={`media-rail ${className}`} data-overflow={overflow || undefined}>
    <button type="button" tabIndex={-1} className="rail-arrow rail-prev" onMouseDown={e => e.preventDefault()} aria-label={`Scroll ${label} back`} disabled={edges.start} hidden={!overflow} onClick={() => page(-1)}><ChevronLeft size={22} /></button>
    <div ref={track} className="rail-track" role="group" aria-label={label} onKeyDown={onKeyDown}
      onFocus={e => { const item = items().find(i => i.contains(e.target as Node)); item?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: smooth() }); }}>
      {children}
    </div>
    <button type="button" tabIndex={-1} className="rail-arrow rail-next" onMouseDown={e => e.preventDefault()} aria-label={`Scroll ${label} forward`} disabled={edges.end} hidden={!overflow} onClick={() => page(1)}><ChevronRight size={22} /></button>
  </div>;
}
